import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import { useBlogs } from "../../../services/blogService";
import type { RootState } from "../../../store";
import { useSelector } from "react-redux";
import { Loader2 } from "lucide-react";

const Blogs = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const isDark = useSelector((state: RootState) => state.theme.darkMode);

  const { data: blogsData, isLoading } = useBlogs({
    locale: currentLanguage,
    status: "published",
    category: selectedCategory === "all" ? undefined : selectedCategory,
  });

  const posts = blogsData?.posts || [];
  const categories = [
    "all",
    ...Array.from(
      new Set(posts.flatMap((post) => post.categories || []).filter(Boolean))
    ),
  ];

  const featuredPost = posts.find((post) => post.is_featured);
  const regularPosts = posts.filter((post) => !post.is_featured);

  return (
    <div
      className={`min-h-screen ${
        isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"
      }`}
    >
      <Header />
      <main>
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`py-24 px-4 sm:px-16 ${
            isDark ? "bg-gray-800" : "bg-gray-50"
          }`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className={`text-4xl md:text-6xl font-light mb-6 tracking-tight ${
                isDark ? "text-gray-100" : "text-gray-900"
              }`}
            >
              {t("blog.heroTitle")}
            </h1>
            <p
              className={`text-xl font-light leading-relaxed max-w-2xl mx-auto ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {t("blog.heroSubtitle")}
            </p>
          </div>
        </motion.section>

        {/* Category Filter */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.6 }}
          className={`py-8 px-4 sm:px-16 border-b ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category!)}
                  className={`px-6 py-2 text-sm font-light tracking-wide transition-colors ${
                    selectedCategory === category
                      ? `${
                          isDark
                            ? "text-gray-100 border-b-2 border-gray-100"
                            : "text-gray-900 border-b-2 border-gray-900"
                        }`
                      : `${
                          isDark
                            ? "text-gray-400 hover:text-gray-200"
                            : "text-gray-500 hover:text-gray-700"
                        }`
                  }`}
                >
                  {category === "all" ? t("blog.allArticles") : category}
                </button>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
          </div>
        )}

        {/* Featured Post */}
        {!isLoading && featuredPost && selectedCategory === "all" && (
          <motion.section
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="py-16 px-4 sm:px-16"
          >
            <div className="max-w-6xl mx-auto">
              <div className="mb-8">
                <span
                  className={`text-sm font-medium uppercase tracking-wider ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {t("blog.featuredArticle")}
                </span>
              </div>
              <div
                className="cursor-pointer group"
                onClick={() =>
                  navigate(`/${currentLanguage}/blogs/${featuredPost.id}`)
                }
              >
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="order-2 md:order-1">
                    <div className="mb-4">
                      {featuredPost.categories && featuredPost.categories.length > 0 && (
                        <span className="inline-block bg-gray-900 text-white px-3 py-1 text-xs font-medium tracking-wide">
                          {featuredPost.categories[0]}
                        </span>
                      )}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 leading-tight group-hover:text-gray-700 transition-colors">
                      {featuredPost.title}
                    </h2>
                    <p className="text-lg text-gray-600 font-light leading-relaxed mb-6">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex items-center space-x-4">
                      {featuredPost.author_image && (
                        <img
                          src={featuredPost.author_image}
                          alt={featuredPost.author || ""}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">
                          {featuredPost.author || "Admin"}
                        </p>
                        <p className="text-sm text-gray-500">
                          {featuredPost.published_at
                            ? new Date(featuredPost.published_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : ""}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="order-1 md:order-2">
                    {featuredPost.featured_image ? (
                      <img
                        src={featuredPost.featured_image}
                        alt={featuredPost.title}
                        className="w-full h-80 md:h-96 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-80 md:h-96 bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400">No image</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.section>
        )}

        {/* Posts Grid */}
        {!isLoading && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="py-16 px-4 sm:px-16"
          >
            <div className="max-w-6xl mx-auto">
              {regularPosts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("blog.noPosts", "No blog posts found")}
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {regularPosts.map((post, index) => (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.1 }}
                      transition={{ duration: 0.5, delay: Math.min(index * 0.1, 1) }}
                      className="group cursor-pointer"
                      onClick={() =>
                        navigate(`/${currentLanguage}/blogs/${post.id}`)
                      }
                    >
                      <div className="mb-4 overflow-hidden">
                        {post.featured_image ? (
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-400 text-sm">No image</span>
                          </div>
                        )}
                      </div>
                      <div className="mb-3">
                        {post.categories && post.categories.length > 0 && (
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {post.categories[0]}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-3 leading-tight group-hover:text-gray-700 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm font-light leading-relaxed mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{post.author || "Admin"}</span>
                        <span>{post.read_time || 5} {t("blog.minRead", "min read")}</span>
                      </div>
                    </motion.article>
                  ))}
                </div>
              )}
            </div>
          </motion.section>
        )}

        {/* Newsletter Signup */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className={`py-16 px-4 sm:px-16 ${
            isDark ? "bg-gray-800 text-gray-100" : "bg-gray-900 text-white"
          }`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-light mb-4">
              {t("blog.stayInformed")}
            </h2>
            <p
              className={`font-light leading-relaxed mb-8 max-w-2xl mx-auto ${
                isDark ? "text-gray-300" : "text-gray-300"
              }`}
            >
              {t("blog.newsletterText")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder={t("blog.emailPlaceholder")}
                className={`flex-1 px-4 py-3 font-light focus:outline-none ${
                  isDark
                    ? "bg-gray-700 text-gray-100 placeholder-gray-400"
                    : "bg-white text-gray-900"
                }`}
              />
              <button
                className={`px-6 py-3 font-light transition-colors ${
                  isDark
                    ? "bg-gray-700 text-gray-100 hover:bg-gray-600"
                    : "bg-white text-gray-900 hover:bg-gray-100"
                }`}
              >
                {t("blog.subscribe")}
              </button>
            </div>
          </div>
        </motion.section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default Blogs;
